import React, { useState, useMemo, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { getGA4Channels, getGA4LandingPages, getGA4Devices } from '../../../api/auth';
import { TrafficByHourBar, TrafficByDayOfWeekBar } from '../ChartsSection';
import { WindowSelector } from '../content-behavior/WindowSelector';
import { fmtNumber, fmtPercent, fmtDuration, fmtDateLabel } from '../shared/format';
import {
  formatLandingPage,
  sortRows, weightedAvg, downloadCsv, downloadPdf, makeExport,
  DRILL_COLORS, TREND_METRICS,
  SortableHeader, PaginationFooter, TableSkeleton, SectionInsightCard, ViewToggle,
} from '../shared';
import ThemedChartTooltip, { ThemedTooltipBox } from '../../shared/ThemedChartTooltip';
import ThinScroll from '../../shared/ThinScroll';
import { SkeletonChart, SkeletonText } from '../../shared/Skeleton';
import { ErrorState, EmptyState } from '../../shared/StateMessage';
import { unwrapItems } from '../../shared/unwrapItems';
import InfoTip from '../../shared/InfoTip';
import useTablePage from '../../../hooks/useTablePage';
import { expandableRowProps } from '../../shared/expandableRow';

/* ============================= Drill-Down Panel (per-day) ============================= */
const DrillDownPanel = ({ date, clientId }) => {
  const [channelData, setChannelData] = useState(null);
  const [landingData, setLandingData] = useState(null);
  const [deviceData, setDeviceData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientId || !date) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getGA4Channels(clientId, date, date).then(resp => resp?.data ?? []).catch(() => []),
      // fn_get_ga4_landing_pages returns an { items, pagination } envelope — unwrap
      // `.items` so the drill-down can sort it (channels/devices are bare arrays).
      getGA4LandingPages(clientId, date, date).then(unwrapItems).catch(() => []),
      getGA4Devices(clientId, date, date).then(resp => resp?.data ?? []).catch(() => []),
    ]).then(([channels, landingPages, devices]) => {
      if (cancelled) return;
      setChannelData(channels);
      setLandingData(landingPages);
      setDeviceData(devices);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [clientId, date]);

  if (loading) {
    return <div className="py-4"><SkeletonText lines={6} /></div>;
  }

  const chData = (channelData || []).filter(row => (row.sessions || 0) > 0).sort((rowA, rowB) => (rowB.sessions || 0) - (rowA.sessions || 0));
  const chTotal = chData.reduce((sum, row) => sum + (row.sessions || 0), 0);
  const topLP = (landingData || []).sort((rowA, rowB) => (rowB.sessions || 0) - (rowA.sessions || 0)).slice(0, 5);
  const dvData = (deviceData || []).filter(row => (row.sessions || 0) > 0);
  const dvTotal = dvData.reduce((sum, row) => sum + (row.sessions || 0), 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">Channel Breakdown</h4>
        {chData.length === 0 ? (
          <div className="text-xs text-gray-400 py-4 text-center">No data</div>
        ) : (
          <div className="space-y-1.5">
            {chData.map((row, index) => {
              const pct = chTotal > 0 ? (row.sessions / chTotal) * 100 : 0;
              return (
                <div key={row.channel} className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: DRILL_COLORS[index % DRILL_COLORS.length] }} />
                  <span className="text-xs text-gray-700 dark:text-gray-300 flex-1 truncate">{row.channel}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">{fmtNumber(row.sessions)}</span>
                  <span className="text-[10px] text-gray-400 dark:text-gray-500 w-10 text-right tabular-nums">{pct.toFixed(1)}%</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">Top 5 Landing Pages</h4>
        {topLP.length === 0 ? (
          <div className="text-xs text-gray-400 py-4 text-center">No data</div>
        ) : (
          <div className="space-y-2">
            {topLP.map((row, index) => (
              <div key={index} className="flex items-start gap-2">
                <span className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 w-4 text-right flex-shrink-0">{index + 1}.</span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-700 dark:text-gray-300 truncate" title={formatLandingPage(row.landing_page)}>{formatLandingPage(row.landing_page)}</div>
                  <div className="text-[10px] text-gray-400 dark:text-gray-500">{fmtNumber(row.sessions)} sessions · {fmtPercent(row.bounce_rate)} bounce</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">Device Split</h4>
        {dvData.length === 0 ? (
          <div className="text-xs text-gray-400 py-4 text-center">No data</div>
        ) : (
          <div>
            <ResponsiveContainer width="100%" height={120}>
              <PieChart>
                <Pie data={dvData.map(row => ({ name: row.device || 'Unknown', value: row.sessions || 0 }))} cx="50%" cy="50%" innerRadius={30} outerRadius={50} paddingAngle={2} dataKey="value">
                  {dvData.map((_, index) => <Cell key={index} fill={DRILL_COLORS[index % DRILL_COLORS.length]} />)}
                </Pie>
                <ThemedChartTooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const datum = payload[0];
                  const pct = dvTotal > 0 ? ((datum.value / dvTotal) * 100).toFixed(1) : 0;
                  return <ThemedTooltipBox title={datum.name}><div>{datum.value.toLocaleString()} ({pct}%)</div></ThemedTooltipBox>;
                }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-2 justify-center mt-1">
              {dvData.map((row, index) => (
                <div key={row.device} className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: DRILL_COLORS[index % DRILL_COLORS.length] }} />
                  {row.device || 'Unknown'} ({dvTotal > 0 ? (((row.sessions || 0) / dvTotal) * 100).toFixed(0) : 0}%)
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ============================= Table ============================= */
const TRAFFIC_PAGE_SIZE = 10;

const TrafficOverviewTable = ({ data, loading, error, clientId, searchQuery = '' }) => {
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });
  const [expandedDate, setExpandedDate] = useState(null);

  const handleSort = (key) => {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };

  const filtered = useMemo(() => {
    if (!searchQuery) return data || [];
    const query = searchQuery.toLowerCase();
    return (data || []).filter(row => (row.date || '').toLowerCase().includes(query) || fmtDateLabel(row.date).toLowerCase().includes(query));
  }, [data, searchQuery]);

  const sorted = useMemo(() => sortRows(filtered, sort.key, sort.dir), [filtered, sort]);
  // This table already reset on [data]; useTablePage keeps that and adds the clamp
  // so the page can never exceed totalPages even for one render.
  const { page, setPage, totalPages, sliceStart, sliceEnd } = useTablePage(sorted.length, TRAFFIC_PAGE_SIZE, [data, searchQuery, sort]);
  const pageRows = useMemo(() => sorted.slice(sliceStart, sliceEnd), [sorted, sliceStart, sliceEnd]);

  const totals = useMemo(() => {
    if (!filtered || filtered.length === 0) return null;
    return {
      sessions: filtered.reduce((sum, row) => sum + (row.sessions || 0), 0),
      users: filtered.reduce((sum, row) => sum + (row.users || 0), 0),
      new_users: filtered.reduce((sum, row) => sum + (row.new_users || 0), 0),
      pageviews: filtered.reduce((sum, row) => sum + (row.pageviews || 0), 0),
      bounce_rate: weightedAvg(filtered, 'bounce_rate', 'sessions'),
      avg_session_duration: weightedAvg(filtered, 'avg_session_duration', 'sessions'),
      engagement_rate: weightedAvg(filtered, 'engagement_rate', 'sessions'),
      conversions: filtered.reduce((sum, row) => sum + (row.conversions || 0), 0),
    };
  }, [filtered]);

  if (error) return <ErrorState message={error} />;
  if (loading) return <TableSkeleton />;
  if (!data || data.length === 0) {
    return <EmptyState message="No traffic data for this period." padding="py-12" />;
  }

  return (
    <div>
    <ThinScroll className="responsive-card-table-wrapper bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-x-auto">
      <table className="responsive-card-table w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
            <SortableHeader label="Date" sortKey="date" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Sessions" sortKey="sessions" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Users" sortKey="users" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="New Users" sortKey="new_users" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Pageviews" sortKey="pageviews" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Bounce Rate" sortKey="bounce_rate" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Avg. Duration" sortKey="avg_session_duration" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Engagement Rate" sortKey="engagement_rate" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Conversions" sortKey="conversions" currentSort={sort} onSort={handleSort} />
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row) => (
            <React.Fragment key={row.date}>
              <tr
                {...expandableRowProps({
                  expanded: expandedDate === row.date,
                  onToggle: () => setExpandedDate(expandedDate === row.date ? null : row.date),
                  label: `details for ${fmtDateLabel(row.date)}`,
                  className: `border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition cursor-pointer ${expandedDate === row.date ? 'bg-blue-50/50 dark:bg-blue-900/10' : 'rc-collapsed'}`,
                })}
              >
                <td data-label="Date" className={`rc-title-cell px-3 py-2.5 text-gray-900 dark:text-gray-100 font-medium whitespace-nowrap ${expandedDate === row.date ? 'rc-title-open' : ''}`}>
                  <span className="inline-flex items-center gap-1.5">
                    <svg className={`w-3 h-3 text-gray-400 transition-transform ${expandedDate === row.date ? 'rotate-90' : ''}`} viewBox="0 0 12 12">
                      <path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    </svg>
                    {fmtDateLabel(row.date)}
                  </span>
                </td>
                <td data-label="Sessions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.sessions)}</td>
                <td data-label="Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.users)}</td>
                <td data-label="New Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.new_users)}</td>
                <td data-label="Pageviews" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.pageviews)}</td>
                <td data-label="Bounce Rate" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtPercent(row.bounce_rate)}</td>
                <td data-label="Avg. Duration" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtDuration(row.avg_session_duration)}</td>
                <td data-label="Engagement Rate" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtPercent(row.engagement_rate)}</td>
                <td data-label="Conversions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.conversions)}</td>
              </tr>
              {expandedDate === row.date && (
                <tr>
                  <td colSpan={9} className="px-3 py-4 bg-gray-50/50 dark:bg-gray-800/30 border-b dark:border-gray-800">
                    <DrillDownPanel date={row.date} clientId={clientId} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/60 font-semibold">
            <td data-label="Totals" className="rc-title-cell rc-title-open px-3 py-2.5 text-gray-900 dark:text-gray-100">Totals</td>
            <td data-label="Sessions" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.sessions)}</td>
            <td data-label="Users" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.users)}</td>
            <td data-label="New Users" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.new_users)}</td>
            <td data-label="Pageviews" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.pageviews)}</td>
            <td data-label="Bounce Rate" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtPercent(totals.bounce_rate)}</td>
            <td data-label="Avg. Duration" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtDuration(totals.avg_session_duration)}</td>
            <td data-label="Engagement Rate" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtPercent(totals.engagement_rate)}</td>
            <td data-label="Conversions" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.conversions)}</td>
          </tr>
        </tfoot>
      </table>
    </ThinScroll>
    <PaginationFooter pagination={{ total_items: sorted.length, total_pages: totalPages }} page={page} perPage={TRAFFIC_PAGE_SIZE} onPageChange={setPage} label="days" />
    </div>
  );
};

/* ============================= Trend Chart ============================= */
const aggregateByWeek = (rows) => {
  const weeks = {};
  for (const row of rows) {
    const date = new Date(row.date + 'T00:00:00');
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    const weekStart = new Date(date);
    weekStart.setDate(diff);
    const key = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`;
    if (!weeks[key]) weeks[key] = { date: key, sessions: 0, users: 0, conversions: 0, _count: 0 };
    weeks[key].sessions += row.sessions || 0;
    weeks[key].users += row.users || 0;
    weeks[key].conversions += row.conversions || 0;
    weeks[key]._count += 1;
  }
  return Object.values(weeks).sort((weekA, weekB) => weekA.date.localeCompare(weekB.date));
};

const TrafficTrendChart = ({ data, loading, error }) => {
  const [metric, setMetric] = useState('sessions');

  const dayCount = data?.length ?? 0;
  const useWeekly = dayCount > 60;

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    const rows = useWeekly ? aggregateByWeek(data) : [...data].sort((rowA, rowB) => rowA.date.localeCompare(rowB.date));
    return rows.map((row) => {
      const date = new Date(row.date + 'T00:00:00');
      return {
        ...row,
        label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        tooltipLabel: date.toLocaleDateString('en-US', useWeekly ? { month: 'short', day: 'numeric' } : { weekday: 'short', month: 'short', day: 'numeric' }),
      };
    });
  }, [data, useWeekly]);

  const activeMeta = TREND_METRICS.find((metricOption) => metricOption.key === metric) || TREND_METRICS[0];

  const distinctWeeks = useMemo(() => {
    if (!data || data.length === 0) return 0;
    const weekSet = new Set();
    for (const row of data) {
      const date = new Date(row.date + 'T00:00:00');
      const year = date.getFullYear();
      const jan1 = new Date(year, 0, 1);
      const weekNum = Math.ceil(((date - jan1) / 86400000 + jan1.getDay() + 1) / 7);
      weekSet.add(`${year}-W${weekNum}`);
    }
    return weekSet.size;
  }, [data]);

  if (error) return <ErrorState message={error} />;

  if (loading) {
    return <SkeletonChart height={280} />;
  }

  if (!data || data.length === 0) {
    return <EmptyState message="No traffic data for this period." padding="py-12" />;
  }

  if (distinctWeeks < 2) {
    return (
      <EmptyState message="Trend data will be available once you have multiple weeks of analytics data." padding="py-12" />
    );
  }

  return (
    <div>
      <div className="flex items-center gap-1 mb-4">
        {TREND_METRICS.map((metricOption) => (
          <button
            key={metricOption.key}
            type="button"
            onClick={() => setMetric(metricOption.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition ${
              metric === metricOption.key
                ? 'text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
            style={metric === metricOption.key ? { backgroundColor: metricOption.color } : undefined}
          >
            {metricOption.label}
          </button>
        ))}
        {useWeekly && (
          <span className="ml-3 text-[11px] text-gray-400 dark:text-gray-500 italic">Aggregated by week</span>
        )}
      </div>

      <div className="rotate-x-labels">
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-gray-200 dark:text-gray-700" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: '#9CA3AF' }}
            interval={Math.max(0, Math.ceil(chartData.length / 7) - 1)}
            angle={-30}
            textAnchor="end"
            height={50}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#9CA3AF' }}
            tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value}
            width={50}
          />
          <ThemedChartTooltip
            formatter={(value) => [Number(value).toLocaleString(), activeMeta.label]}
            labelFormatter={(label, payload) => payload?.[0]?.payload?.tooltipLabel || label}
          />
          <Line
            type="monotone"
            dataKey={metric}
            stroke={activeMeta.color}
            strokeWidth={2}
            dot={chartData.length <= 31}
            activeDot={{ r: 5, strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
};

/* ============================= Insight summary ============================= */
export const getTrafficSummary = (data, channelData) => {
  if (!data || data.length < 2) return { summary: [], warnings: [] };
  const summary = [];
  const warnings = [];
  const totalSessions = data.reduce((sum, row) => sum + (row.sessions || 0), 0);
  // Bounce rate is a RATE, so it must be weighted by sessions — a 5-session day
  // cannot count as much as a 5,000-session day. The plain mean this replaced
  // (sum / data.length) let a couple of quiet days dominate: for days of 5/5000/8
  // sessions at 95%/40%/92% bounce it returned 75.7% instead of the true 40.1%,
  // crossing the 60% threshold below and fabricating the amber warning.
  // Matches the totals row in this same file (see weightedAvg above).
  const avgBounce = weightedAvg(data, 'bounce_rate', 'sessions');
  const bestDay = [...data].sort((rowA, rowB) => (rowB.sessions || 0) - (rowA.sessions || 0))[0];
  if (bestDay) {
    const pct = totalSessions > 0 ? ((bestDay.sessions / totalSessions) * 100).toFixed(1) : 0;
    const label = new Date(bestDay.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    summary.push(`Peak traffic day was ${label} with ${(bestDay.sessions || 0).toLocaleString()} sessions (${pct}% of total).`);
  }
  // weightedAvg returns null when no row carries sessions — don't warn on no data.
  if (avgBounce != null && avgBounce > 0.6) {
    summary.push(`Average bounce rate is ${(avgBounce * 100).toFixed(1)}%.`);
    warnings.push({ color: 'amber', text: `High bounce rate (${(avgBounce * 100).toFixed(1)}%) — review landing page relevance and load times` });
  }
  // Zero-conversion channels with non-trivial traffic — likely tracking gap or wasted spend
  if (channelData?.length) {
    const ZERO_CONV_MIN_SESSIONS = 500;
    const byChannel = {};
    for (const row of channelData) {
      if (!row?.channel) continue;
      if (!byChannel[row.channel]) byChannel[row.channel] = { sessions: 0, conversions: 0 };
      byChannel[row.channel].sessions += row.sessions || 0;
      byChannel[row.channel].conversions += row.conversions || 0;
    }
    const offenders = Object.entries(byChannel)
      .filter(([, stats]) => stats.sessions >= ZERO_CONV_MIN_SESSIONS && stats.conversions === 0)
      .sort((entryA, entryB) => entryB[1].sessions - entryA[1].sessions);
    for (const [channel, stats] of offenders) {
      const isPaid = /paid|cpc|ads/i.test(channel);
      warnings.push({
        color: isPaid ? 'red' : 'amber',
        text: `${channel}: ${stats.sessions.toLocaleString()} sessions, 0 conversions — check tracking or campaign performance`,
      });
    }
  }
  return { summary, warnings };
};

/* ============================= Export helpers ============================= */
// One column spec drives CSV + PDF (can't drift). CSV emits raw values + full
// headers; PDF emits formatted strings + abbreviated headers.
const TRAFFIC_EXPORT = makeExport([
  { header: 'Date', get: (row) => row.date },
  { header: 'Sessions', get: (row) => row.sessions, pdf: (row) => fmtNumber(row.sessions) },
  { header: 'Users', get: (row) => row.users, pdf: (row) => fmtNumber(row.users) },
  { header: 'New Users', get: (row) => row.new_users, pdf: (row) => fmtNumber(row.new_users) },
  { header: 'Pageviews', pdfHeader: 'PVs', get: (row) => row.pageviews, pdf: (row) => fmtNumber(row.pageviews) },
  { header: 'Bounce Rate', pdfHeader: 'Bounce', get: (row) => (row.bounce_rate != null ? (row.bounce_rate * 100).toFixed(1) + '%' : ''), pdf: (row) => fmtPercent(row.bounce_rate) },
  { header: 'Avg Duration', pdfHeader: 'Dur.', get: (row) => row.avg_session_duration, pdf: (row) => fmtDuration(row.avg_session_duration) },
  { header: 'Engagement Rate', pdfHeader: 'Engage', get: (row) => (row.engagement_rate != null ? (row.engagement_rate * 100).toFixed(1) + '%' : ''), pdf: (row) => fmtPercent(row.engagement_rate) },
  { header: 'Conversions', pdfHeader: 'Conv.', get: (row) => row.conversions, pdf: (row) => fmtNumber(row.conversions) },
]);

export const exportTrafficCsv = (trafficData, rangeStart, rangeEnd) => {
  if (!trafficData?.length) return;
  downloadCsv(TRAFFIC_EXPORT.headers, trafficData.map(TRAFFIC_EXPORT.toCsvRow), `ga4-traffic-${rangeStart}-to-${rangeEnd}.csv`);
};

export const exportTrafficPdf = (trafficData, rangeStart, rangeEnd) => {
  if (!trafficData?.length) return;
  const dateRange = rangeStart && rangeEnd ? `${rangeStart} to ${rangeEnd}` : '';
  downloadPdf('Traffic Overview', TRAFFIC_EXPORT.pdfHeaders, trafficData.map(TRAFFIC_EXPORT.toPdfRow), `ga4-traffic-${rangeStart}-to-${rangeEnd}.pdf`, dateRange);
};

/* ============================= Tab component ============================= */
const TrafficOverviewTab = ({
  trafficData, trafficLoading, trafficError, channelData, selectedClientId, searchQuery,
  hourlyTrafficData, hourlyTrafficLoading, hourlyTrafficError, hourlyTrafficWindows, selectedHourlyTrafficWindow, onHourlyTrafficWindowChange,
  dayOfWeekData, dayOfWeekLoading, dayOfWeekError, dayOfWeekWindows, selectedDayOfWeekWindow, onDayOfWeekWindowChange,
  rangeStart, rangeEnd,
}) => {
  const [view, setView] = useState('table');

  const sharedPatternWindows = useMemo(() => {
    if (!hourlyTrafficWindows?.length || !dayOfWeekWindows?.length) return [];
    const dowSet = new Set(dayOfWeekWindows.map(window => `${window.date_start}|${window.date_end}`));
    return hourlyTrafficWindows.filter(window => dowSet.has(`${window.date_start}|${window.date_end}`));
  }, [hourlyTrafficWindows, dayOfWeekWindows]);
  const patternWindowsMatch = sharedPatternWindows.length > 0;

  return (
    <div>
      <SectionInsightCard title="Traffic Insight" {...getTrafficSummary(trafficData, channelData)} />

      {/* Traffic Patterns (analysis-window driven) */}
      <div className="relative mb-6 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
              <span>Traffic Patterns</span>
              <InfoTip text="Sessions broken out by hour-of-day and day-of-week. Use it to time campaigns and staff support for peak hours." />
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1">
              <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" /></svg>
              Based on pre-computed analysis windows — use the window selector below to explore different periods.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-gray-500 dark:text-gray-400">Window:</span>
            {patternWindowsMatch ? (
              <WindowSelector windows={sharedPatternWindows} selected={selectedHourlyTrafficWindow} onChange={(window) => { onHourlyTrafficWindowChange(window); onDayOfWeekWindowChange(window); }} rangeStart={rangeStart} rangeEnd={rangeEnd} />
            ) : (
              <div className="flex items-center gap-3">
                <WindowSelector windows={hourlyTrafficWindows} selected={selectedHourlyTrafficWindow} onChange={onHourlyTrafficWindowChange} rangeStart={rangeStart} rangeEnd={rangeEnd} />
                <WindowSelector windows={dayOfWeekWindows} selected={selectedDayOfWeekWindow} onChange={onDayOfWeekWindowChange} rangeStart={rangeStart} rangeEnd={rangeEnd} />
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TrafficByHourBar data={hourlyTrafficData} loading={hourlyTrafficLoading} error={hourlyTrafficError} />
          <TrafficByDayOfWeekBar data={dayOfWeekData} loading={dayOfWeekLoading} error={dayOfWeekError} />
        </div>
      </div>

      <ViewToggle view={view} onChange={setView} />
      {view === 'table'
        ? <TrafficOverviewTable data={trafficData} loading={trafficLoading} error={trafficError} clientId={selectedClientId} searchQuery={searchQuery} />
        : <TrafficTrendChart data={trafficData} loading={trafficLoading} error={trafficError} />
      }
    </div>
  );
};

export default TrafficOverviewTab;
